import React from "react";
import { Dog, Bike } from "lucide-react";

const Kid = ({ left, top, color, dur = 6, amp = 40, delay = 0, flip = false }) => (
  <div
    className="kid-wander absolute z-[12]"
    style={{ left: `${left}%`, top: `${top}%`, "--dur": `${dur}s`, "--amp": `${amp}px`, animationDelay: `${delay}s` }}
  >
    <div className="kid-hop" style={{ animationDelay: `${delay}s`, transform: flip ? "scaleX(-1)" : "none" }}>
      <div className="mx-auto h-2 w-2 rounded-full border border-kirana-brown bg-[#F1C27D]" />
      <div className="mx-auto mt-0.5 h-3 w-2.5 rounded-sm border border-kirana-brown" style={{ background: color }} />
      <div className="mx-auto flex w-2.5 justify-between">
        <span className="h-1.5 w-1 bg-[#3A2E23]" />
        <span className="h-1.5 w-1 bg-[#3A2E23]" />
      </div>
    </div>
  </div>
);

const KID_COLORS = ["#C84B31", "#457B9D", "#2D6A4F", "#E9C46A", "#6A0572"];
const Kids = () => (
  <>
    {[
      { left: 30, top: 70, dur: 7, amp: 50, delay: 0 },
      { left: 62, top: 66, dur: 6, amp: -40, delay: 0.6, flip: true },
      { left: 46, top: 82, dur: 8, amp: 60, delay: 1.2 },
      { left: 74, top: 76, dur: 5.5, amp: -30, delay: 0.3, flip: true },
      { left: 18, top: 80, dur: 6.5, amp: 35, delay: 0.9 },
    ].map((k, i) => <Kid key={i} color={KID_COLORS[i % KID_COLORS.length]} {...k} />)}
  </>
);

const Sky = ({ from, to, h = 42 }) => (
  <div className="absolute inset-x-0 top-0" style={{ height: `${h}%`, background: `linear-gradient(180deg, ${from}, ${to})` }} />
);
const Ground = ({ from, to, top = 42 }) => (
  <>
    <div className="absolute inset-x-0 bottom-0" style={{ top: `${top}%`, background: `linear-gradient(180deg, ${from}, ${to})` }} />
    <div className="absolute inset-x-0 h-1.5 bg-kirana-brown/30" style={{ top: `${top}%` }} />
  </>
);

// tall colony house with balcony
const ColonyHouse = ({ left, storeys = 2, roof = "#C84B31", body = "#E9C46A" }) => (
  <div className="absolute top-[4%]" style={{ left: `${left}%` }}>
    <div className="h-3 w-24 -translate-x-1 rounded-t" style={{ background: roof }} />
    <div className="w-24 border-2 border-kirana-brown" style={{ height: `${storeys * 26}px`, background: body }}>
      {Array.from({ length: storeys }).map((_, i) => (
        <div key={i} className="flex items-center justify-between px-1.5" style={{ height: "26px" }}>
          <div className="h-3 w-4 border border-kirana-brown bg-[#8AB4C4]" />
          <div className="h-3 w-4 border border-kirana-brown bg-[#8AB4C4]" />
        </div>
      ))}
    </div>
    {/* balcony rail */}
    <div className="-mt-3 h-2 w-24 border-x-2 border-t-2 border-kirana-brown bg-[repeating-linear-gradient(90deg,#6E5238_0_3px,transparent_3px_7px)]" />
  </div>
);

const Lane = () => (
  <>
    <Sky from="#A7C7D6" to="#E4CE9A" />
    <Ground from="#BFA06B" to="#9C7A4E" />
    <ColonyHouse left={4} storeys={3} roof="#C84B31" body="#E9C46A" />
    <ColonyHouse left={78} storeys={3} roof="#2D6A4F" body="#DDB067" />
    <ColonyHouse left={26} storeys={2} roof="#6A0572" body="#E6B85C" />
    {/* clotheslines */}
    <div className="absolute left-[10%] top-[16%] flex gap-1">
      {["#457B9D", "#E76F51", "#2D6A4F", "#C84B31"].map((c, i) => <div key={i} className="h-5 w-3 rounded-b" style={{ background: c }} />)}
    </div>
    {/* pole + wires */}
    <div className="absolute left-[50%] top-[6%] h-20 w-1.5 bg-kirana-brown" />
    <div className="absolute left-[10%] top-[10%] h-0.5 w-[40%] rotate-2 bg-kirana-brown/70" />
    <div className="absolute left-[50%] top-[12%] h-0.5 w-[40%] -rotate-1 bg-kirana-brown/70" />
    {/* tree */}
    <div className="absolute left-[64%] top-[30%]"><div className="h-12 w-12 rounded-full bg-[#4C7A3F]" /><div className="mx-auto -mt-1 h-8 w-2 bg-[#6E5238]" /></div>
    {/* scooter + bike + dog */}
    <div className="absolute bottom-[12%] left-[60%] h-6 w-12 rounded-t-xl border-2 border-kirana-brown bg-[#457B9D]" />
    <Bike className="absolute bottom-[14%] left-[24%] h-8 w-8 text-kirana-brown" strokeWidth={2} />
    <Dog className="absolute bottom-[8%] left-[44%] h-6 w-6 text-[#6E5238]" strokeWidth={2} />
  </>
);

const DustGround = () => (
  <>
    <Sky from="#BFE3F2" to="#EAD9A6" h={38} />
    <Ground from="#D3B378" to="#B4894F" top={38} />
    {/* far boundary wall */}
    <div className="absolute left-[3%] right-[3%] top-[36%] h-5 rounded-sm border-2 border-kirana-brown bg-[#C2A878]" />
    {/* big chalk court */}
    <div className="absolute left-1/2 top-[52%] h-28 w-64 -translate-x-1/2 rounded-sm border-2 border-white/70" />
    <div className="absolute left-1/2 top-[52%] h-28 w-0.5 -translate-x-1/2 bg-white/60" />
    <div className="absolute left-[calc(50%-64px)] top-[59%] h-14 w-14 rounded-full border-2 border-white/50" />
    <div className="absolute left-[calc(50%+22px)] top-[59%] h-14 w-14 rounded-full border-2 border-white/50" />
    {/* corner trees */}
    <div className="absolute left-[6%] top-[30%]"><div className="h-12 w-12 rounded-full bg-[#4C7A3F]" /><div className="mx-auto -mt-1 h-8 w-2 bg-[#6E5238]" /></div>
    <div className="absolute right-[6%] top-[30%]"><div className="h-12 w-12 rounded-full bg-[#5A8A47]" /><div className="mx-auto -mt-1 h-8 w-2 bg-[#6E5238]" /></div>
  </>
);

const Plot = () => (
  <>
    <Sky from="#D9D0B0" to="#E0CFA0" h={40} />
    <Ground from="#C2A878" to="#9C7A4E" top={40} />
    {/* broken boundary wall with gaps */}
    {[6, 30, 62].map((l, i) => (
      <div key={i} className="absolute top-[37%] h-6 border-2 border-kirana-brown bg-[#B08968]" style={{ left: `${l}%`, width: "18%", opacity: 0.9 }} />
    ))}
    {/* rubble / brick piles */}
    {[[16, 72], [40, 80], [70, 74], [84, 66]].map(([l, t], i) => (
      <div key={i} className="absolute flex gap-0.5" style={{ left: `${l}%`, top: `${t}%` }}>
        <span className="h-2 w-3 bg-[#9C6644]" /><span className="h-2 w-3 bg-[#B08968]" /><span className="h-2 w-3 bg-[#8B5A2B]" />
      </div>
    ))}
    {/* weed tufts */}
    {[24, 52, 78, 90].map((l, i) => (
      <div key={i} className="absolute bottom-[8%] h-4 w-3" style={{ left: `${l}%`, background: "radial-gradient(circle at 50% 100%, #5A8A47 40%, transparent 41%)" }} />
    ))}
    {/* lone bare tree + old tyre */}
    <div className="absolute left-[12%] top-[26%]"><div className="mx-auto h-10 w-1.5 bg-[#5A4632]" /><div className="-mt-9 ml-2 h-6 w-0.5 rotate-45 bg-[#5A4632]" /><div className="-mt-6 mr-2 h-6 w-0.5 -rotate-45 bg-[#5A4632]" /></div>
    <div className="absolute bottom-[10%] right-[16%] h-8 w-8 rounded-full border-4 border-[#2B2B2B]" />
  </>
);

const Street = () => (
  <>
    <Sky from="#BFD7E0" to="#DCC79A" h={38} />
    <div className="absolute inset-x-0 bottom-0 top-[38%]" style={{ background: "linear-gradient(180deg,#8D99AE,#5A6270)" }} />
    <div className="absolute inset-x-0 top-[38%] h-1.5 bg-kirana-brown/30" />
    {/* shop shutters row */}
    <div className="absolute left-[4%] right-[4%] top-[24%] flex gap-1">
      {["#C84B31", "#457B9D", "#2D6A4F", "#E76F51", "#6A0572", "#9C6644"].map((c, i) => (
        <div key={i} className="h-14 flex-1 border-2 border-kirana-brown" style={{ background: `${c}` }}>
          <div className="h-full w-full bg-[repeating-linear-gradient(180deg,rgba(0,0,0,0.18)_0_3px,transparent_3px_6px)]" />
        </div>
      ))}
    </div>
    {/* poles + wires */}
    {[16, 50, 84].map((l, i) => <div key={i} className="absolute top-[8%] h-16 w-1.5 bg-kirana-brown" style={{ left: `${l}%` }} />)}
    <div className="absolute left-[16%] top-[10%] h-0.5 w-[68%] bg-kirana-brown/70" />
    {/* center road dashes */}
    <div className="absolute inset-x-[8%] top-[64%] h-1.5 bg-[repeating-linear-gradient(90deg,#FDF6E3_0_22px,transparent_22px_44px)]" />
    {/* cycle + cart */}
    <Bike className="absolute bottom-[12%] left-[26%] h-9 w-9 text-kirana-cream" strokeWidth={2} />
    <div className="absolute bottom-[10%] right-[24%] h-6 w-16 rounded-sm border-2 border-kirana-brown bg-[#9C6644]" />
  </>
);

const Roof = () => (
  <>
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#F4A261 0%,#E76F51 55%,#C05a3e 100%)" }} />
    {/* sun */}
    <div className="absolute right-[14%] top-[10%] h-14 w-14 rounded-full bg-[#FFD166] shadow-[0_0_40px_12px_rgba(255,209,102,0.6)]" />
    {/* terraces at varying heights */}
    {[[6, 46, 38], [30, 40, 44], [56, 52, 34], [78, 44, 40]].map(([l, t, h], i) => (
      <div key={i} className="absolute border-2 border-kirana-brown bg-[#B98E52]" style={{ left: `${l}%`, top: `${t}%`, width: "22%", height: `${h}%` }}>
        {/* parapet */}
        <div className="h-2 w-full bg-[repeating-linear-gradient(90deg,#8B5A2B_0_6px,#B98E52_6px_12px)]" />
      </div>
    ))}
    {/* water tanks + antennas */}
    {[16, 62].map((l, i) => <div key={i} className="absolute top-[36%] h-6 w-8 rounded-t-md border-2 border-kirana-brown bg-[#457B9D]" style={{ left: `${l}%` }} />)}
    {[38, 84].map((l, i) => (
      <div key={i} className="absolute top-[30%]" style={{ left: `${l}%` }}>
        <div className="mx-auto h-6 w-0.5 bg-kirana-brown" />
        <div className="-mt-6 flex gap-1"><span className="h-0.5 w-3 bg-kirana-brown" /><span className="h-0.5 w-2 bg-kirana-brown" /></div>
      </div>
    ))}
    {/* kites in sky */}
    {[["#E63946", 20, 8], ["#2A9D8F", 46, 4], ["#F72585", 70, 10], ["#457B9D", 88, 6]].map(([c, l, t], i) => (
      <div key={i} className="absolute h-4 w-4 rotate-45 border border-kirana-brown/60" style={{ left: `${l}%`, top: `${t}%`, background: c }} />
    ))}
  </>
);

// ── Playground / Garden (no buildings) ──────────────────────────────
const Tree = ({ left, top, s = 1, c = "#4C7A3F" }) => (
  <div className="absolute" style={{ left: `${left}%`, top: `${top}%`, transform: `scale(${s})` }}>
    <div className="h-14 w-14 rounded-full" style={{ background: c }} />
    <div className="h-10 w-12 -mt-6 ml-1 rounded-full" style={{ background: c, filter: "brightness(1.08)" }} />
    <div className="mx-auto -mt-2 h-9 w-2.5 rounded-sm bg-[#6E5238]" />
  </div>
);

const Swings = ({ left, top }) => (
  <div className="absolute" style={{ left: `${left}%`, top: `${top}%` }}>
    {/* A-frame */}
    <div className="relative h-16 w-24">
      <span className="absolute left-1 top-0 h-16 w-1.5 origin-top rotate-[14deg] rounded bg-[#B24A2E]" />
      <span className="absolute left-3 top-0 h-16 w-1.5 origin-top -rotate-[14deg] rounded bg-[#B24A2E]" />
      <span className="absolute right-1 top-0 h-16 w-1.5 origin-top rotate-[14deg] rounded bg-[#B24A2E]" />
      <span className="absolute right-3 top-0 h-16 w-1.5 origin-top -rotate-[14deg] rounded bg-[#B24A2E]" />
      <span className="absolute left-2 right-2 top-0 h-1.5 rounded bg-[#7a5230]" />
      {/* swinging seat with a kid */}
      <div className="swing-seat absolute left-1/2 top-1 origin-top">
        <span className="mx-auto block h-9 w-[2px] bg-[#3a2617]" />
        <span className="mx-auto -mt-0.5 block h-2 w-6 rounded-sm bg-[#2A9D8F]" />
        <span className="mx-auto -mt-2 block h-2.5 w-2.5 rounded-full bg-[#F1C27D]" />
      </div>
      {/* empty second seat */}
      <div className="absolute left-[64%] top-1">
        <span className="mx-auto block h-10 w-[2px] bg-[#3a2617]" />
        <span className="mx-auto -mt-0.5 block h-2 w-6 rounded-sm bg-[#E76F51]" />
      </div>
    </div>
  </div>
);

const Slide = ({ left, top }) => (
  <div className="absolute" style={{ left: `${left}%`, top: `${top}%` }}>
    <div className="relative h-16 w-20">
      {/* platform + ladder */}
      <span className="absolute right-1 top-2 h-14 w-1.5 bg-[#457B9D]" />
      <span className="absolute right-6 top-2 h-3 w-6 bg-[#E9C46A]" />
      {[0, 1, 2, 3].map((i) => <span key={i} className="absolute right-[6px] w-4 h-[2px] bg-[#7a5230]" style={{ top: `${20 + i * 12}px` }} />)}
      {/* slope */}
      <span className="absolute left-0 top-5 h-2.5 w-16 origin-right rotate-[24deg] rounded bg-[#F4A261]" />
    </div>
  </div>
);

const SeeSaw = ({ left, top }) => (
  <div className="absolute" style={{ left: `${left}%`, top: `${top}%` }}>
    <div className="relative h-8 w-28">
      <span className="absolute left-1/2 bottom-0 h-4 w-4 -translate-x-1/2 rounded-t-full bg-[#6A0572]" />
      <span className="seesaw-plank absolute left-1/2 bottom-3 h-1.5 w-28 -translate-x-1/2 origin-center rounded bg-[#E76F51]" />
    </div>
  </div>
);

const Bench = ({ left, top }) => (
  <div className="absolute" style={{ left: `${left}%`, top: `${top}%` }}>
    <div className="h-2.5 w-14 rounded-sm bg-[#8B5A2B]" />
    <div className="-mt-3 h-2 w-14 rounded-sm bg-[#9C6644]" />
    <div className="flex w-14 justify-between"><span className="h-3 w-1.5 bg-[#6E5238]" /><span className="h-3 w-1.5 bg-[#6E5238]" /></div>
  </div>
);

const FlowerBed = ({ left, top }) => (
  <div className="absolute flex gap-1" style={{ left: `${left}%`, top: `${top}%` }}>
    {["#E63946", "#F4C542", "#F72585", "#457B9D", "#E63946"].map((c, i) => (
      <div key={i} className="flex flex-col items-center">
        <span className="h-2 w-2 rounded-full" style={{ background: c }} />
        <span className="h-2 w-[2px] bg-[#3f7d3f]" />
      </div>
    ))}
  </div>
);

const Park = () => (
  <>
    {/* warm golden-hour sky */}
    <Sky from="#FCE0B0" to="#F6B26B" h={46} />
    {/* sun glow */}
    <div className="absolute right-[18%] top-[8%] h-16 w-16 rounded-full bg-[#FFD79A] shadow-[0_0_60px_20px_rgba(255,200,120,0.55)]" />
    {/* grassy garden ground */}
    <Ground from="#8FBF6B" to="#5E8C4F" top={46} />
    {/* hedge boundary along the horizon (replaces buildings) */}
    <div className="absolute inset-x-0 top-[41%] h-6 bg-[repeating-linear-gradient(90deg,#3f7d3f_0_14px,#4C8A47_14px_28px)]" />
    {/* winding light path */}
    <div className="absolute left-[6%] right-[6%] top-[74%] h-6 -rotate-1 rounded-full bg-[#d9c39a]/50" />
    {/* equipment + garden props (background) */}
    <Tree left={2} top={22} s={1.1} c="#4C7A3F" />
    <Tree left={90} top={20} s={1.2} c="#5A8A47" />
    <Swings left={7} top={30} />
    <SeeSaw left={40} top={34} />
    <Slide left={80} top={30} />
    <Bench left={12} top={82} />
    <FlowerBed left={62} top={82} />
    <FlowerBed left={30} top={88} />
    {/* lamp post */}
    <div className="absolute left-[93%] top-[62%]">
      <span className="mx-auto block h-16 w-1.5 bg-[#3a3a3a]" />
      <span className="absolute -top-1 left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-[#FFE39A] shadow-[0_0_12px_4px_rgba(255,220,140,0.7)]" />
    </div>
    {/* soft warm wash */}
    <div className="absolute inset-0 bg-[#ff9a3a]/8 mix-blend-multiply" />
    <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 120px rgba(60,30,0,0.22)" }} />
  </>
);

// A few kids playing on the grass (kept light so the scene stays clean).
const ParkKids = () => (
  <div className="pointer-events-none absolute inset-0 z-[12]">
    {[
      { left: 34, top: 78, dur: 7, amp: 40, delay: 0 },
      { left: 68, top: 74, dur: 6, amp: -34, delay: 0.6, flip: true },
      { left: 50, top: 90, dur: 8, amp: 46, delay: 1.2 },
    ].map((k, i) => <Kid key={i} color={KID_COLORS[i % KID_COLORS.length]} {...k} />)}
  </div>
);

export const MohallaDecor = ({ decor }) => (
  <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
    {decor === "park" && <Park />}
    {decor === "lane" && <Lane />}
    {decor === "ground" && <DustGround />}
    {decor === "plot" && <Plot />}
    {decor === "street" && <Street />}
    {decor === "roof" && <Roof />}
    {decor === "park" && <ParkKids />}
    {decor !== "roof" && decor !== "park" && <Kids />}
  </div>
);
